import { Module } from '../Module';
import { GetPostLoginNotificationsRequest, IGetPostLoginNotificationsResult } from '../ServerApi/requests/GetPostLoginNotificationsRequest';
import { ModalService, ModalSize } from './ModalService';
import { ServerService } from './ServerService';
import { PostLoginNotificationCtrl } from '../controllers/PostLoginNotificationCtrl';
import { FeatureFlags } from '../FeatureFlags';
import * as _ from 'underscore';
import * as queryString from 'query-string';

interface IPostLoginNotificationScope extends ng.IScope {
  notificationsUrl: any;
  resourceLocationUrl: any;
  uriQueryJsonString: string;
}

export class PostLoginNotifications {

  private notificationsEnabled: boolean;
  private notificationsShown: boolean; // Track whether we've already shown the notifications
  static readonly DEFAULT_CACHE_DAYS = 1;      // Default number of days to cache a timestamp in LocalStorage
  static readonly ENDING_TIME_KEY = 'endingTime';
  static readonly STARTING_TIME_KEY = 'startingTime';
  static readonly HASHED_CONTENT_KEY = 'hashedContent';

  public static $inject = ['$sce', '$q', 'ModalService', '$rootScope', 'ServerService'];

  constructor(private $sce: ng.ISCEService,
              private $q: ng.IQService,
              private modal: ModalService,
              private $rootScope: ng.IScope,
              private ServerService: ServerService
  ) {
    this.notificationsShown = false;
    this.notificationsEnabled = false;
  }

  public enableNotifications(): void {
    this.notificationsEnabled = true;
  }

  public getEnableNotifications(): boolean {
    return this.notificationsEnabled;
  }

  public showNotificationsIfEnabled(): ng.IPromise<any> {
    if (!this.notificationsEnabled || this.notificationsShown) {
      return this.$q.when(null);
    }
    return this.fetchNotificationsUrl().then((url: string) => {
      if (url && !FeatureFlags.dismissPostLoginNotificationEnabled) {
        const scope = <IPostLoginNotificationScope>this.$rootScope.$new();
        scope.notificationsUrl = this.$sce.trustAsResourceUrl(url);

        return this.modal.open({
          scope: scope,
          templateUrl: 'postLoginNotifications.html',
          size: ModalSize.fitToContent
        }).result.finally(() => scope.$destroy());
      }
      else if (url && FeatureFlags.dismissPostLoginNotificationEnabled) {
        const cachedUrl = this.removeSearchKey(url);
        let shouldDispplay = false;

        const cachedQueryJson = JSON.parse(localStorage.getItem(cachedUrl));
        let curQueryJson = this.getUrlQueryStringJson(url);

        if (cachedQueryJson == null) {
          // There is no cached timestamp, so display the notification
          if (curQueryJson == null) {
            curQueryJson = this.getDefaultUriQueryJson();
          }
          shouldDispplay = true;
        }
        else if (curQueryJson == null) {
          // There is a cached timestamp, but the url query string is missing or mis-formatted.
          // So use TODAY to decide whether to display the notification
          let curTimestamp = new Date();
          let diff = curTimestamp.valueOf() - cachedQueryJson[PostLoginNotifications.ENDING_TIME_KEY];
          if (diff >= 0) {
            localStorage.removeItem(cachedUrl);
            curQueryJson = this.getDefaultUriQueryJson();
            shouldDispplay = true;
          }
        }
        else if (!_.isEqual(cachedQueryJson, curQueryJson)) {
          // There is some change in the current query string. 
          // So there is a new notification, and remove the old one
          localStorage.removeItem(cachedUrl);
          shouldDispplay = true;
        }

        if (shouldDispplay) {
          const scope = <IPostLoginNotificationScope>this.$rootScope.$new();
          scope.resourceLocationUrl = cachedUrl;
          scope.uriQueryJsonString =  JSON.stringify(curQueryJson);
          scope.notificationsUrl = this.$sce.trustAsResourceUrl(url);

          return this.modal.open({
            scope: scope,
            templateUrl: 'postLoginNotifications.html',
            size: ModalSize.fitToContent,
            controller: PostLoginNotificationCtrl
          }).result.finally(() => scope.$destroy());
        }
      }
    }).finally(() => this.notificationsShown = true); // Suppress in case of future remounting

  }

  public fetchNotificationsUrl(): ng.IPromise<string> {
    return this.ServerService.sendRequest(new GetPostLoginNotificationsRequest())
      .then((result: IGetPostLoginNotificationsResult) => result.contentUrl);
  }

  // Get the url without the search keys
  public removeSearchKey(url: string) {
    let resultUrl = null;
    if (url) {
      const urlObject = queryString.parseUrl(url);
      resultUrl = urlObject.url;
    }
    return resultUrl;
  }

  // parse the query string, and put them into a json object
  public getUrlQueryStringJson(url: string) {
    let json = null;

    const urlObject = queryString.parseUrl(url);

    let value = urlObject.query[PostLoginNotifications.STARTING_TIME_KEY];
    // Convert seconds to milliseconds 
    const startTime = this.getValidDateTime(value) * 1000;

    value = urlObject.query[PostLoginNotifications.ENDING_TIME_KEY];
    // Convert seconds to milliseconds 
    const endTime = this.getValidDateTime(value) * 1000;

    const content = urlObject.query[PostLoginNotifications.HASHED_CONTENT_KEY];

    // If query parameters are available
    if (startTime > 0 && endTime > 0 && startTime < endTime && content != null) {
      const curDate = new Date();
      // If the time range is valid
      if (curDate.valueOf() < endTime) {
        json = {startingTime: startTime, endingTime: endTime, hashedContent: content};
      }
    }
    return json;
  }

  // Return the default json objec that has the 24 hours' time range from now
  private getDefaultUriQueryJson() {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + PostLoginNotifications.DEFAULT_CACHE_DAYS);

    const json = { startingTime: today.getTime(), endingTime: tomorrow.getTime(), hashedContent: '' };
    return json;
  }

  // Parse the date time, and return O for an invalid value
  private getValidDateTime(timeString: string) {
    var num = 0;
    if (timeString != null) {
      num = parseInt(timeString) || 0;
    }
    return num;
  }
}

Module.instance().service('PostLoginNotifications', PostLoginNotifications);
